import React, { useMemo, useState } from "react";
import {
  Grid,
  List,
  ListItem,
  ListItemIcon,
  Box,
  TextField,
  Typography,
  Button,
  Divider,
  Popover,
  styled,
  InputAdornment,
  CircularProgress,
  ListSubheader,
} from "@material-ui/core";
import { StyledCheckBox } from "./CustomTheme.web";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import { searchIcon } from "./commonAssets/assets";
import "./style.css";

interface StoreItem {
  id: string;
  type: string;
  attributes: {
    id: number;
    store_name: string;
    name_translation: string;
    from_store_group?: boolean;
  };
}

interface AreaItem {
  id: number;
  area_name: string;
  store_managements: {
    data: StoreItem[];
  };
}

interface RegionItem {
  id: number;
  branch_name: string;
  areas: AreaItem[];
}

interface Props {
  regionAreaData: RegionItem[];
  currentSelectedAreaId: string[];
  currentSelectedStoreIdByArea: Record<number, string[]>;
  isLoading: boolean;
  buttonLabel: string;
  handleSelectArea: (areaId: string) => void;
  handleSelectAllAreas: () => void;
  handleSelectStore: (storeId: string, areaId: string) => void;
  handleSelectAllStoresInArea: (areaId: string) => void;
  handleClearAll: () => void;
}

const StoreGroupDropdown = ({
  regionAreaData,
  currentSelectedAreaId,
  currentSelectedStoreIdByArea,
  isLoading,
  buttonLabel,
  handleSelectArea,
  handleSelectAllAreas,
  handleSelectStore,
  handleSelectAllStoresInArea,
  handleClearAll,
}: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [areaSearchTxt, setAreaSearchTxt] = useState("");
  const [storeSearchTxt, setStoreSearchTxt] = useState("");

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "store-group-dropdown" : undefined;

  const totalAreas = useMemo(
    () => regionAreaData.reduce((count, region) => count + region.areas.length, 0),
    [regionAreaData]
  );

  const allAreasSelected =
    totalAreas > 0 && currentSelectedAreaId.length === totalAreas;

  // Left panel: areas filtered by the area search (matches region or area name).
  const filteredRegions = useMemo(() => {
    const query = areaSearchTxt.trim().toLowerCase();
    if (!query) return regionAreaData;
    return regionAreaData
      .map((region) => {
        const regionMatches = region.branch_name.toLowerCase().includes(query);
        const areas = region.areas.filter(
          (area) =>
            regionMatches || area.area_name.toLowerCase().includes(query)
        );
        return { ...region, areas };
      })
      .filter((region) => region.areas.length > 0);
  }, [regionAreaData, areaSearchTxt]);

  // Right panel: stores of a selected area filtered by the store search.
  const getFilteredStores = (area: AreaItem) => {
    const query = storeSearchTxt.trim().toLowerCase();
    if (!query) return area.store_managements.data;
    return area.store_managements.data.filter((store) =>
      store.attributes.store_name.toLowerCase().includes(query)
    );
  };

  const isStoreSelected = (areaId: number, storeId: number) =>
    currentSelectedStoreIdByArea[areaId]?.includes(String(storeId)) || false;

  const areAllStoresSelected = (area: AreaItem) => {
    const stores = area.store_managements.data;
    if (stores.length === 0) return false;
    return stores.every((store) =>
      isStoreSelected(area.id, store.attributes.id)
    );
  };

  const renderSearchField = (
    value: string,
    onChange: (value: string) => void,
    placeholder: string,
    testId: string
  ) => (
    <TextField
      fullWidth
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      variant="outlined"
      size="small"
      style={{ marginBottom: 12 }}
      value={value}
      data-test-id={testId}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <img src={searchIcon} className="searchicnon" />
          </InputAdornment>
        ),
      }}
    />
  );

  const renderLeftPanel = () => (
    <List style={{ paddingTop: 0 }}>
      <StyleTypography variant="subtitle1">Regions &gt; Areas</StyleTypography>
      {renderSearchField(
        areaSearchTxt,
        setAreaSearchTxt,
        "Search Areas",
        "area_search"
      )}
      <ListItem
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          paddingTop: 0,
          paddingLeft: 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          <Box
            data-test-id="area_selectall"
            onClick={() => handleSelectAllAreas()}
          >
            <ListItemIcon style={{ minWidth: "30px" }}>
              <StyledCheckBox checked={allAreasSelected} />
            </ListItemIcon>
          </Box>
          <StyleListItemText>Select All Areas</StyleListItemText>
        </div>
        <StyleClearDiv
          onClick={() => {
            setAreaSearchTxt("");
            handleClearAll();
          }}
          data-test-id="clear_all"
        >
          Clear
        </StyleClearDiv>
      </ListItem>
      <StyleScrollDiv>
        {filteredRegions.length === 0 ? (
          <StylePlaceholderTypography>No Areas Found</StylePlaceholderTypography>
        ) : (
          filteredRegions.map((region) => (
            <div key={region.id}>
              <StyleRegionSubheader disableSticky>
                {region.branch_name}
              </StyleRegionSubheader>
              {region.areas.map((area) => (
                <ListItem
                  key={area.id}
                  button
                  data-test-id={`area_${area.id}`}
                  onClick={() => handleSelectArea(String(area.id))}
                  style={{ paddingTop: 0, paddingLeft: 12 }}
                >
                  <ListItemIcon style={{ minWidth: "30px" }}>
                    <StyledCheckBox
                      checked={currentSelectedAreaId.includes(String(area.id))}
                    />
                  </ListItemIcon>
                  <StyleListItemTextArea>{area.area_name}</StyleListItemTextArea>
                </ListItem>
              ))}
            </div>
          ))
        )}
      </StyleScrollDiv>
    </List>
  );

  const renderRightPanel = () => {
    const storeQuery = storeSearchTxt.trim().toLowerCase();
    const isSearching = storeQuery.length > 0;
    // While searching, list stores across ALL areas so a store can be found and
    // selected without its area being selected first. Otherwise show the
    // selected areas' stores.
    const areasToShow = isSearching
      ? regionAreaData
          .flatMap((region) => region.areas)
          .filter((area) =>
            area.store_managements.data.some((store) =>
              store.attributes.store_name.toLowerCase().includes(storeQuery)
            )
          )
      : regionAreaData
          .flatMap((region) => region.areas)
          .filter(
            (area) =>
              currentSelectedAreaId.includes(String(area.id)) ||
              (currentSelectedStoreIdByArea[area.id]?.length ?? 0) > 0
          );

    return (
      <List style={{ paddingTop: 0, paddingLeft: 0 }}>
        <StyleTypography variant="subtitle1">
          Areas &gt; Stores
        </StyleTypography>
        {renderSearchField(
          storeSearchTxt,
          setStoreSearchTxt,
          "Search Stores",
          "store_search"
        )}
        <StyleScrollDiv>
          {areasToShow.length === 0 ? (
            <StylePlaceholderTypography style={{ paddingLeft: 12 }}>
              {isSearching
                ? "No Stores Found"
                : "No Area Selected — search a store or pick an area"}
            </StylePlaceholderTypography>
          ) : (
            areasToShow.map((area) => (
            <div key={area.id}>
              <ListItem
                style={{ paddingTop: 0, paddingLeft: 0 }}
                button
                data-test-id={`selected_area_${area.id}`}
                onClick={() => handleSelectAllStoresInArea(String(area.id))}
              >
                <ListItemIcon style={{ minWidth: "30px" }}>
                  <StyledCheckBox checked={areAllStoresSelected(area)} />
                </ListItemIcon>
                <StyleListItemTextArea>{area.area_name}</StyleListItemTextArea>
              </ListItem>
              {getFilteredStores(area).length === 0 ? (
                <StylePlaceholderTypography style={{ paddingLeft: 30 }}>
                  No Stores
                </StylePlaceholderTypography>
              ) : (
                getFilteredStores(area).map((store) => (
                  <ListItem
                    key={store.attributes.id}
                    button
                    data-test-id={`store_${store.attributes.id}`}
                    onClick={() =>
                      handleSelectStore(
                        String(store.attributes.id),
                        String(area.id)
                      )
                    }
                    style={{ paddingTop: 0, paddingLeft: 30 }}
                  >
                    <ListItemIcon style={{ minWidth: "30px" }}>
                      <StyledCheckBox
                        checked={isStoreSelected(area.id, store.attributes.id)}
                      />
                    </ListItemIcon>
                    {isStoreSelected(area.id, store.attributes.id) ? (
                      <StyleListItemTextWithSelected>
                        {store.attributes.store_name}
                      </StyleListItemTextWithSelected>
                    ) : (
                      <StyleListItemText>
                        {store.attributes.store_name}
                      </StyleListItemText>
                    )}
                  </ListItem>
                ))
              )}
            </div>
            ))
          )}
        </StyleScrollDiv>
      </List>
    );
  };

  return (
    <div>
      <StyleButton
        aria-describedby={id}
        variant="outlined"
        onClick={handleClick}
        data-test-id="open_dropdown"
      >
        <Typography
          style={{
            fontSize: "16px",
            fontWeight: 400,
            textTransform: "capitalize",
          }}
        >
          {buttonLabel}
        </Typography>
        <div>
          <ArrowDropDownIcon />
        </div>
      </StyleButton>
      <Popover
        id={id}
        open={open}
        style={{ zIndex: 99 }}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <div className="customBoxGrid" style={{ padding: 10 }}>
          {isLoading ? (
            <StyleLoaderDiv>
              <CircularProgress style={{ width: 40, height: 40 }} />
            </StyleLoaderDiv>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                {renderLeftPanel()}
              </Grid>
              <Grid item xs={1}>
                <Divider orientation="vertical" variant="middle" />
              </Grid>
              <Grid item xs={5}>
                {renderRightPanel()}
              </Grid>
            </Grid>
          )}
        </div>
      </Popover>
    </div>
  );
};

export default StoreGroupDropdown;

const StyleTypography = styled(Typography)({
  fontWeight: 500,
  fontSize: "14px",
  color: "#000000",
  marginBottom: "10px",
});

const StyleListItemText = styled(Typography)({
  fontWeight: 400,
  fontSize: "14px",
  color: "#0F172A",
});

const StyleListItemTextArea = styled(Typography)({
  fontWeight: 400,
  fontSize: "14px",
  color: "#D7471D",
});

const StyleListItemTextWithSelected = styled(Typography)({
  fontWeight: 400,
  fontSize: "14px",
  color: "#204B9C",
});

const StyleRegionSubheader = styled(ListSubheader)({
  color: "#000000",
  fontSize: "14px",
  fontWeight: 600,
  lineHeight: "28px",
  paddingLeft: 0,
  backgroundColor: "#FFFFFF",
});

const StylePlaceholderTypography = styled(Typography)({
  fontWeight: 400,
  fontSize: "13px",
  color: "#64748B",
  padding: "8px 0",
});

const StyleClearDiv = styled(Box)({
  color: "#DC2626",
  fontSize: 16,
  fontWeight: 500,
  textDecoration: "underline",
  cursor: "pointer",
});

const StyleButton = styled(Button)({
  display: "flex",
  justifyContent: "space-between",
  width: "275px",
  height: "54px",
  borderRadius: "8px",
  "@media only screen and (max-width: 1024px)": {
    height: "38px",
  },
});

const StyleScrollDiv = styled("div")({
  overflowY: "scroll",
  overflowX: "hidden",
  maxHeight: "275px",

  "&::-webkit-scrollbar": {
    width: "6px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#888",
    borderRadius: "4px",
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "#f1f1f1",
  },

  scrollbarWidth: "thin",
  scrollbarColor: "#888 #f1f1f1",
});

const StyleLoaderDiv = styled("div")({
  padding: "15px",
  display: "flex",
  justifyContent: "center",
});
